import React, { useState, useRef } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import Select from "react-select";
import "./BasicDetailsModal.css";
import WorkExperienceModal from "../WorkExperienceModal/WorkExperienceModal";

const BasicDetailsModal = ({ show, handleClose }) => {
  // --- Form Functional States ---
  const [formData, setFormData] = useState({
    name: "Smita Lad",
    gender: "Female",
    expYears: "3",
    expMonths: "8",
    location: "Nagpur",
    nationality: "Indian",
  });

  // Photo Upload States
  const [avatarPreview, setAvatarPreview] = useState("SL");
  const [hasUploadedImage, setHasUploadedImage] = useState(false);
  const fileInputRef = useRef(null);

  // --- Work Experience Modal State ---
  const [showWorkModal, setShowWorkModal] = useState(false);

  // --- React-Select Configuration ---
  const languageOptions = [
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
    { value: "marathi", label: "Marathi" },
    { value: "telugu", label: "Telugu" },
    { value: "tamil", label: "Tamil" },
    { value: "gujarati", label: "Gujarati" },
  ];

  const [selectedLanguages, setSelectedLanguages] = useState([
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
    { value: "marathi", label: "Marathi" },
  ]);

  const locationOptions = [
    { value: "nagpur", label: "Nagpur" },
    { value: "mumbai", label: "Mumbai" },
    { value: "pune", label: "Pune" },
    { value: "hyderabad", label: "Hyderabad" },
    { value: "bangalore", label: "Bangalore" },
    { value: "delhi", label: "Delhi" },
    { value: "chennai", label: "Chennai" },
  ];

  const nationalityOptions = [
    { value: "indian", label: "Indian" },
    { value: "american", label: "American" },
    { value: "canadian", label: "Canadian" },
    { value: "british", label: "British" },
    { value: "australian", label: "Australian" },
  ];

  const [selectedLocation, setSelectedLocation] = useState(
    locationOptions.find((item) => item.value === "nagpur"),
  );

  const [selectedNationality, setSelectedNationality] = useState(
    nationalityOptions.find((item) => item.value === "indian"),
  );

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#9b51e0" : "#dcdde1",
      boxShadow: "none",
      "&:hover": {
        borderColor: state.isFocused ? "#9b51e0" : "#c8d6e5",
      },
      fontSize: "15px",
      minHeight: "40px",
      borderRadius: "4px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#f1f2f6",
      borderRadius: "4px",
      border: "1px solid #e1e2e6",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#2f3542",
      fontSize: "14px",
      padding: "3px 6px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#a4b0be",
      "&:hover": {
        backgroundColor: "#ff4757",
        color: "#ffffff",
        borderRadius: "0 3px 3px 0",
      },
    }),
  };

  // --- Input & Toggle Handlers ---
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const stateMapping = {
      formBasicName: "name",
      formBasicLocation: "location",
      formBasicNationality: "nationality",
    };
    const key = stateMapping[id] || id;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const clearField = (fieldKey) => {
    setFormData((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const triggerFileBrowser = () => fileInputRef.current.click();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
        setHasUploadedImage(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Switcher logic: close this modal, open the work modal
  const handleOpenWorkModal = () => {
    handleClose(); // Close BasicDetailsModal
    setShowWorkModal(true); // Open WorkExperienceModal
  };

  const handleCloseWorkModal = () => {
    setShowWorkModal(false);
  };

  const handleSaveSubmit = (e) => {
    e.preventDefault();
    console.log("Submitting Data Payload:", {
      ...formData,
      profileImage: avatarPreview,
      languages: selectedLanguages.map((item) => item.label),
    });
    handleClose();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        scrollable
        size="lg"
        dialogClassName="common-modal basic-details-modal"
      >
        <Modal.Header closeButton className="basic-details-modal__header">
          <Modal.Title className="basic-details-modal__title">
            Basic details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="basic-details-modal__body">
          <Form onSubmit={handleSaveSubmit}>
            {/* Avatar Row Panel */}
            <div className="bd-avatar-panel d-flex align-items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                accept="image/*"
                style={{ display: "none" }}
              />
              <div className="bd-avatar-panel__box">
                {hasUploadedImage ? (
                  <img
                    src={avatarPreview}
                    alt="Profile preview"
                    className="bd-avatar-img-preview"
                  />
                ) : (
                  avatarPreview
                )}
              </div>
              <Button
                variant="link"
                className="bd-avatar-panel__link p-0"
                onClick={triggerFileBrowser}
              >
                Change photo
              </Button>
            </div>

            {/* Name Field */}
            <Form.Group className="mb-4" controlId="formBasicName">
              <Form.Label className="bd-form-label">
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="bd-form-input"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            {/* Gender Selection */}
            <Form.Group className="mb-4">
              <Form.Label className="bd-form-label">Gender</Form.Label>
              <div className="d-flex gap-2">
                {["Male", "Female", "Prefer not to say"].map((g) => (
                  <Button
                    key={g}
                    type="button"
                    variant="outline-secondary"
                    className={`bd-pill-btn ${formData.gender === g ? "active" : ""}`}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, gender: g }))
                    }
                  >
                    {g}
                  </Button>
                ))}
              </div>
            </Form.Group>

            {/* Total Experience Dropdowns */}
            <Form.Group className="mb-4">
              <Form.Label className="bd-form-label">
                Total Experience <span className="text-danger">*</span>
              </Form.Label>
              <Row>
                <Col>
                  <Form.Select
                    className="bd-form-select"
                    value={formData.expYears}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        expYears: e.target.value,
                      }))
                    }
                  >
                    <option value="1">1 year</option>
                    <option value="2">2 years</option>
                    <option value="3">3 years</option>
                    <option value="4">4 years</option>
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Select
                    className="bd-form-select"
                    value={formData.expMonths}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        expMonths: e.target.value,
                      }))
                    }
                  >
                    <option value="0">0 months</option>
                    <option value="4">4 months</option>
                    <option value="8">8 months</option>
                  </Form.Select>
                </Col>
              </Row>
            </Form.Group>

            {/* Current Location Field */}
            <Form.Group className="mb-4">
              <Form.Label className="bd-form-label">
                Current Location <span className="text-danger">*</span>
              </Form.Label>

              <Select
                options={locationOptions}
                value={selectedLocation}
                onChange={setSelectedLocation}
                styles={customSelectStyles}
                placeholder="Select Current Location"
                isSearchable
                classNamePrefix="react-select"
              />
            </Form.Group>

            {/* Nationality Field */}
            <Form.Group className="mb-4">
              <Form.Label className="bd-form-label">
                Nationality <span className="text-danger">*</span>
              </Form.Label>

              <Select
                options={nationalityOptions}
                value={selectedNationality}
                onChange={setSelectedNationality}
                styles={customSelectStyles}
                placeholder="Select Nationality"
                isSearchable
                classNamePrefix="react-select"
              />
            </Form.Group>

            {/* React-Select Component */}
            <Form.Group className="mb-4">
              <Form.Label className="bd-form-label">Languages Known</Form.Label>
              <Select
                isMulti
                options={languageOptions}
                value={selectedLanguages}
                onChange={setSelectedLanguages}
                styles={customSelectStyles}
                placeholder="Select known languages..."
                classNamePrefix="react-select"
              />
            </Form.Group>

            {/* Nested Mini Embedded Job Work Preview Component */}
            <div className="bd-nested-work-preview d-flex justify-content-between align-items-start">
              <div className="d-flex gap-3">
                <div className="bd-nested-work-preview__icon">💼</div>
                <div>
                  <span className="bd-nested-work-preview__tag">
                    Work Experience
                  </span>
                  <h5 className="bd-nested-work-preview__title">
                    Frontend Development Engineer
                  </h5>
                  <p className="bd-nested-work-preview__meta">
                    Profcyma Global Solutions • Jun 2023 - Present • Notice
                    Period: 15 days • Salary: INR 3.2L (Annually)
                  </p>
                </div>
              </div>
              {/* Added onClick handler to trigger the transition */}
              <span
                className="bd-nested-work-preview__edit"
                onClick={handleOpenWorkModal}
                role="button"
                aria-label="Edit work experience"
              >
                ✏️
              </span>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="basic-details-modal__footer">
          <Button
            variant="primary"
            className="bd-btn-save"
            onClick={handleSaveSubmit}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Render the work experience layout instance conditionally */}
      <WorkExperienceModal
        show={showWorkModal}
        handleClose={handleCloseWorkModal}
      />
    </>
  );
};

export default BasicDetailsModal;
